import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, IconButton, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DialogShell from '../components/design-system/DialogShell';
import { fieldSx, labelSx, estiloTabla, btnPrimary } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

export default function Especialidades() {
  const rol = sessionStorage.getItem('rol') || '';
  const [lista, setLista] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    try {
      const r = await axios.get(`${API}/api/especialidades`, { params: { todas: 1 } });
      setLista(r.data);
    } catch (err) { console.error(err); }
  }

  function abrirNuevo() {
    setEditando(null);
    setNombre('');
    setError('');
    setDialogOpen(true);
  }

  function abrirEditar(esp) {
    setEditando(esp.id);
    setNombre(esp.nombre);
    setError('');
    setDialogOpen(true);
  }

  function cerrar() { setDialogOpen(false); }

  async function guardar() {
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return; }
    try {
      if (editando) {
        await axios.put(`${API}/api/especialidades/${editando}`, { nombre });
      } else {
        await axios.post(`${API}/api/especialidades`, { nombre });
      }
      cerrar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    }
  }

  async function desactivar(id) {
    if (!window.confirm('¿Desactivar esta especialidad? No se borra: deja de estar disponible para nuevos médicos y turnos, pero se conserva en el historial.')) return;
    try {
      await axios.delete(`${API}/api/especialidades/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al desactivar');
    }
  }

  async function reactivar(id) {
    try {
      await axios.post(`${API}/api/especialidades/${id}/reactivar`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reactivar');
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MedicalServicesIcon sx={{ color: 'var(--c-acento)' }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
            Especialidades
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo} sx={btnPrimary}>
          Nueva especialidad
        </Button>
      </Box>

      <TableContainer component={Paper} sx={estiloTabla}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
              <TableCell>Nombre</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: '#b0b0b0', py: 4 }}>
                  No hay especialidades registradas. Creá la primera.
                </TableCell>
              </TableRow>
            ) : lista.map(esp => (
              <TableRow key={esp.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' }, opacity: esp.activo ? 1 : 0.6 }}>
                <TableCell sx={{ fontWeight: 500 }}>{esp.nombre}</TableCell>
                <TableCell>
                  {esp.activo
                    ? <Chip label="Activa" color="success" size="small" />
                    : <Chip label="Inactiva" size="small" />}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => abrirEditar(esp)} sx={{ color: 'var(--c-acento)' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {rol === 'admin' && (esp.activo ? (
                    <IconButton onClick={() => desactivar(esp.id)} sx={{ color: '#f48fb1' }} title="Desactivar">
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => reactivar(esp.id)} sx={{ color: '#81c784' }} title="Reactivar">
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogShell
        open={dialogOpen}
        onClose={cerrar}
        maxWidth="xs"
        title={editando ? 'Editar especialidad' : 'Nueva especialidad'}
        subtitle="Nombre que aparecerá en turnos y en el perfil del médico"
        actions={
          <>
            <Button onClick={cerrar} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
            <Button variant="contained" onClick={guardar} sx={btnPrimary}>Guardar</Button>
          </>
        }
      >
        <Box>
          <Typography sx={labelSx}>Nombre *</Typography>
          <TextField
            fullWidth autoFocus value={nombre}
            onChange={e => { setNombre(e.target.value); setError(''); }}
            onKeyPress={e => e.key === 'Enter' && guardar()}
            sx={fieldSx}
            placeholder="Ej: Clínica médica, Pediatría, Cardiología..."
          />
        </Box>
        {error && (
          <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1 }}>{error}</Typography>
        )}
      </DialogShell>
    </Box>
  );
}
